import React, { useState } from 'react';

import moment from 'moment';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import PersonalInfo from 'components/application/PersonalInfo';

import { findCurrentFieldValue } from 'components/helpers/personnel';
import { resourceShape } from 'components/helpers/serialisableResources';

const moreBreakpoint = 3;

export default function PersonnelDetailsTab({
  defaultDetails,
  domainFieldAttributes,
  fieldValues,
}) {
  const [showAllFields, setShowAllFields] = useState(false);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'mobileTrainingProfile.components.personnelDetailsScreen.PersonnelDetailsTab',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const formattedFieldValues = Object.values(fieldValues).reduce(
    (acc, value) => {
      acc[value.relationships.fieldAttribute.data.id] = {
        value:
          value.attributes.valueType === 'date' ?
            moment.parseZone(value.attributes.value).toDate()
          : value.attributes.value,
        valueType: value.attributes.valueType,
        fieldOptionId:
          value.relationships.fieldOption.data &&
          value.relationships.fieldOption.data.id,
      };
      return acc;
    },
    {},
  );

  const allDetails = defaultDetails
    .filter((field) => field.text)
    .concat(
      domainFieldAttributes.data.reduce((customDetails, fieldAttribute) => {
        const value = findCurrentFieldValue({
          fieldAttribute,
          fieldOptions: domainFieldAttributes.included.fieldOption,
          fieldValues: formattedFieldValues,
        });

        if (value) {
          customDetails.push({
            label: fieldAttribute.attributes.name,
            text: value,
          });
        }

        return customDetails;
      }, []),
    );

  const visibleDetails =
    allDetails.length < moreBreakpoint || showAllFields ?
      allDetails
    : allDetails.slice(0, moreBreakpoint);

  return (
    <div className='tw-rounded-lg tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-px-4 tw-py-6'>
      {allDetails.length > 0 ?
        <>
          {visibleDetails.map((dataPoint, index) => (
            <PersonalInfo
              className='tw-mb-2'
              copyable={dataPoint.label === i18nTShared('email')}
              hideIcon={true}
              isCompact={true}
              key={index}
              label={dataPoint.label}
              preserveWhitespace={true}
              text={dataPoint.text}
            />
          ))}
          {!showAllFields && allDetails.length > moreBreakpoint && (
            <div className='tw-flex tw-items-center'>
              <button
                className='app-link tw-bg-transparent tw-text-blue-500 hover:tw-text-blue-300 active:tw-text-blue-300'
                onClick={() => setShowAllFields(true)}
              >
                {i18nTShared('showAllWithMoreCount', {
                  count: allDetails.length - moreBreakpoint,
                })}
              </button>
            </div>
          )}
        </>
      : <div className='tw-text-grey-500'>{i18nT('emptyState')}</div>}
    </div>
  );
}

PersonnelDetailsTab.propTypes = {
  defaultDetails: PropTypes.array.isRequired,
  domainFieldAttributes: PropTypes.shape({
    data: PropTypes.arrayOf(resourceShape('fieldAttribute')).isRequired,
    included: PropTypes.shape({
      fieldOption: PropTypes.objectOf(resourceShape('fieldOption')).isRequired,
    }).isRequired,
  }).isRequired,
  fieldValues: PropTypes.arrayOf(resourceShape('fieldValue')).isRequired,
};
